import { Box, FormControl, FormHelperText, FormLabel, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DynamicPurchasingSystemType,
  type DpsSchemeDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { ThemedDatePicker } from "../../../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { endOfDay, startOfUtcDay } from "./schemeValidityDates";

interface Props {
  eventId: string;
  usage: DynamicPurchasingSystemType | null;
  scheme: DpsSchemeDto | null;
  isEditable: boolean;
}

// t('Only the contracting authorities named in the notice may buy through the system')
// t('Contracting authorities not named in the notice may also buy through the system')
const USAGE_OPTIONS = [
  {
    value: DynamicPurchasingSystemType.OnlyListedBuyers,
    descriptionKey: "Only the contracting authorities named in the notice may buy through the system",
  },
  {
    value: DynamicPurchasingSystemType.AlsoUnlistedBuyers,
    descriptionKey: "Contracting authorities not named in the notice may also buy through the system",
  },
] as const;

// Announced once and binding from then on, which is why the fields lock at publication.
export const DpsSchemeFields = ({ eventId, usage, scheme, isEditable }: Props) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  // The period saves as a pair, so the first date the buyer picks waits here for the second.
  const [draft, setDraft] = useState<Partial<DpsSchemeDto>>({});
  const validFrom = scheme?.validFrom ?? draft.validFrom ?? null;
  const validTo = scheme?.validTo ?? draft.validTo ?? null;

  const setValidity = (next: Partial<DpsSchemeDto>) => {
    const merged: Partial<DpsSchemeDto> = {
      validFrom: validFrom ?? undefined,
      validTo: validTo ?? undefined,
      ...next,
    };
    setDraft(merged);
    if (!merged.validFrom || !merged.validTo) return;
    updateField({ publicContext: { dpsScheme: { validFrom: merged.validFrom, validTo: merged.validTo } } });
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5" mb="8">
      <Heading as="h3" size="sm" mb="1">
        {t("The dynamic purchasing system")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mb="5">
        {t("Announced once. Suppliers can apply for the whole period, and every purchase runs among those admitted.")}
      </Text>

      <FormControl mb="6">
        <FormLabel fontSize="sm">{t("Who may buy through the system?")}</FormLabel>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
          {USAGE_OPTIONS.map((option) => (
            <SelectableCard
              key={option.value}
              isSelected={usage === option.value}
              isDisabled={!isEditable}
              onClick={() => {
                if (isEditable) updateField({ publicContext: { dynamicPurchasingSystem: option.value } });
              }}
            >
              <Text fontWeight="semibold" fontSize="sm">
                {t(option.value)}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t(option.descriptionKey)}
              </Text>
            </SelectableCard>
          ))}
        </SimpleGrid>
      </FormControl>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="6">
        <FormControl>
          <FormLabel fontSize="sm">{t("The system runs from")}</FormLabel>
          <ThemedDatePicker
            date={validFrom}
            isDisabled={!isEditable}
            onChange={(date) => setValidity({ validFrom: date ? startOfUtcDay(date) : undefined })}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">{t("until")}</FormLabel>
          <ThemedDatePicker
            date={validTo}
            isDisabled={!isEditable}
            onChange={(date) => setValidity({ validTo: date ? endOfDay(date) : undefined })}
          />
          <FormHelperText>
            {t("Suppliers can apply right up to this date. Extending it later requires a change notice.")}
          </FormHelperText>
        </FormControl>
      </SimpleGrid>
    </Box>
  );
};

